import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import {
  TableFilterSelect,
  InputContainer,
  Typography,
  Button,
  SearchableSelect,
} from '../../design-system';
import { containerTypes, INPUT_ERRORS, offerStatusOptions } from '../../const';
import { i18n } from '../../i18n';
import { extendContainerValidator, showToRoles } from '../../utils';
import { belongsToOptions, clientYardService } from '../../services';
import { User } from '../../types';
import { CloseIconButton, DashboardHeader } from '../dashboard';

interface FiltersPopupProps {
  isOpen: boolean;
}

const FiltersPopup = styled.div<FiltersPopupProps>`
  display: grid;
  grid-template-rows: max-content 1fr;

  height: 100%;
  width: 100%;
  background-color: #f5f6fa;

  padding: var(--spacing-3) var(--spacing-6) var(--spacing-6);
  grid-row-gap: var(--spacing-3);

  position: absolute;
  top: 0;
  right: -100%;
  transition: right 0.3s;
  z-index: 1000;

  ${(props) =>
    props.isOpen &&
    css`
      right: 0;
    `}
`;

const FiltersInputs = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-gap: var(--spacing-6);

  padding-right: var(--spacing-6);
  margin-right: calc(var(--spacing-6) * -1);
  overflow-y: auto;
`;

interface Props extends FiltersPopupProps {
  user: User;
  offerDirection?: 'sent' | 'received';
  number?: string;
  type?: string;
  status?: string;
  buyerClientId?: string;
  clientName?: string;
  setIsOpen: any;
  setStates: any;
  states: any;
}

export const MobileFilters: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  setStates,
  user,
  offerDirection,
  states,
  ...filteredStates
}: Props) => {
  const [number, setNumber] = useState('');
  const [type, setType] = useState('All');
  const [status, setStatus] = useState('All');
  const [buyerClientId, setBuyerClientId] = useState('');
  const [clientName, setClientName] = useState('');

  const isFiltersChange =
    states.number !== number ||
    states.type !== type ||
    states.status !== status ||
    states.buyerClientId !== buyerClientId;

  useEffect(() => {
    if (!isOpen) return;

    setNumber(filteredStates.number || '');
    setType(filteredStates.type || 'All');
    setStatus(filteredStates.status || 'All');
    setBuyerClientId(filteredStates.buyerClientId || '');
    setClientName(filteredStates.clientName || '');
  }, [isOpen]);

  const handleApplyFiltersClick = () => {
    setStates('force', {
      number,
      type,
      status,
      buyerClientId,
    });
    setIsOpen(!isOpen);
  };

  return (
    <FiltersPopup isOpen={isOpen}>
      <DashboardHeader isSmallScreen={true}>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.marketplace.filters')}
        </Typography>
        <CloseIconButton onClick={() => setIsOpen(false)} />
      </DashboardHeader>
      <FiltersInputs>
        <InputContainer
          label={i18n.t('label.containerNumber')}
          data-size={'medium'}
          value={number}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            setNumber(value);
          }}
          helperText={
            number.length !== 0 && number.length < 3
              ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER_MIN
              : !extendContainerValidator(number)
              ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER_SHORT
              : ''
          }
          alwaysShowHelperText
        />
        <TableFilterSelect
          label={i18n.t('label.type')}
          options={containerTypes}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <TableFilterSelect
          label={i18n.t('label.status')}
          options={offerStatusOptions(offerDirection)}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        />
        {showToRoles('adminRead', user) && (
          <SearchableSelect
            selectedOptionLabel={clientName}
            label={i18n.t('label.buyerClient')}
            onSelect={(obj: any) => {
              setClientName(obj.label || '');
              setBuyerClientId(obj.id || '');
            }}
            queryFn={() => {
              if (
                showToRoles(
                  [
                    'YARD_COMPANY_ADMIN',
                    'YARD_COMPANY_EMPLOYEE',
                    'YARD_COMPANY_VIEWER',
                    'YARD_MANAGER',
                    'YARD_DISPATCHER',
                  ],
                  user,
                )
              )
                return clientYardService.assignedClients({
                  yardCompanyId: user.yardCompanyEmployee?.yardCompanyId,
                  id: user.yardEmployee?.yardId,
                });

              if (showToRoles(['SUPER_ADMIN'], user)) return belongsToOptions.Client('', true);

              return () => [];
            }}
            queryKey={[
              'clients',
              'assigned-clients' +
                user.yardEmployee?.yardId +
                user.yardCompanyEmployee?.yardCompanyId,
            ]}
            selectedAreasEqaul={{ key: 'id', keys: [buyerClientId] }}
            withClearButton
          />
        )}
        <Button size={'medium'} onClick={handleApplyFiltersClick} disabled={!isFiltersChange}>
          {i18n.t('button.applyFilters')}
        </Button>
      </FiltersInputs>
    </FiltersPopup>
  );
};
